import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { Icon } from '../../foundation/primitives';
import { FailOverlay, JudgementModal, MinigameHud, StartGate } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

// 非空文字列のみ採用（ホストは raw config を渡す＝Zod default 充填なし。型ゆらぎを自前で吸収する）
const pickText = (value, fallback) => (typeof value === 'string' && value.trim() !== '' ? value : fallback);

// プレイ条件（timeLimitMs / maxMistakes）の正規化: 正の有限数のみ有効。
// step スキーマの既定 0（=制限なし）と不正値は null に揃える（ゼロ config 回帰安全）
const toPlayLimit = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : null;

const Template07Contradiction = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_contradiction_07', templateId = 7 }) => {
  // config 不在時の既定値（従来のデモ内容と同一。解説文も旧ハードコードと同一文字列）。
  const defaultConfig = {
    facts: [
      { id: 'fact-1', text: '被害者は23時に帰宅した' },
      { id: 'fact-2', text: '犯行時刻は22時〜23時' },
    ],
    statements: [
      { id: 'stmt-1', text: '私は22時から自宅にいました', isContradiction: false },
      { id: 'stmt-2', text: '被害者とは会っていません', isContradiction: false },
      {
        id: 'stmt-3',
        text: '23時半に被害者を見かけました',
        isContradiction: true,
        contradictionExplanation: '「23時半に被害者を見かけた」→ 犯行時刻は22〜23時なのに？',
      },
    ],
    wrongSelectionMessage: 'これは矛盾ではない',
  };
  // hostConfigの参照変化でリセットされないよう、JSON文字列化してメモ化キーにする
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const next = { ...defaultConfig, ...(hostConfig ?? {}) };
    if (!Array.isArray(next.facts) || next.facts.length === 0) {
      next.facts = defaultConfig.facts;
    }
    if (!Array.isArray(next.statements) || next.statements.length === 0) {
      next.statements = defaultConfig.statements;
    }
    if (typeof next.wrongSelectionMessage !== 'string' || next.wrongSelectionMessage.length === 0) {
      next.wrongSelectionMessage = defaultConfig.wrongSelectionMessage;
    }
    return next;
  }, [configKey]);
  const backgroundStyle = useMinigameBackground(hostConfig);
  const facts = resolvedConfig.facts;
  const statements = resolvedConfig.statements;

  // R1 体験文法: StartGate / MinigameHud の見出しと説明（config 優先・既定は常体の一行）
  const title = pickText(resolvedConfig.title, '証言の矛盾を探せ');
  const instruction = pickText(resolvedConfig.instructionText, '既知の事実と証言を照合し、矛盾を突け');
  const failMessageConfig =
    typeof resolvedConfig.failMessage === 'object' && resolvedConfig.failMessage !== null ? resolvedConfig.failMessage : {};

  const [selected, setSelected] = useState(null);
  const [gameState, setGameState] = useState('playing');
  const [feedback, setFeedback] = useState(null);
  // 成功モーダルの解説文用に、矛盾を発見した時点の statement を保持する
  // （成功確定までの待ち時間中に selected が変わっても解説がずれないように）。
  const [foundStatement, setFoundStatement] = useState(null);
  // R1 体験文法: 開始ゲート。step が showTutorial===false を明示した時だけ最初から開けておく
  const [gateOpen, setGateOpen] = useState(() => hostMeta?.ui?.showTutorial !== false);
  const [mistakeCount, setMistakeCount] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [failReason, setFailReason] = useState(null); // 'timeout' | 'mistakes'
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  // プレイ条件: config 優先 → step(hostMeta)。0 / 不正値は「制限なし」（従来挙動のまま）
  const timeLimitMs = toPlayLimit(hostConfig?.timeLimitMs ?? hostMeta?.timeLimitMs);
  const mistakeLimit = toPlayLimit(hostConfig?.maxMistakes ?? hostMeta?.maxMistakes);

  const failGame = (reason) => {
    setFailReason(reason);
    // success との競合は「先に確定した方が勝つ」（playing の時だけ fail に落とす）
    setGameState((prev) => (prev === 'playing' ? 'fail' : prev));
  };

  // 実動タイマー: ゲート表示中と矛盾発見後（成功確定待ち）は停止。満了で fail
  // （limit 未設定なら remainingMs=null で無効＝従来どおりタイマーなし）
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs: timeLimitMs,
    running: !gateOpen && gameState === 'playing' && foundStatement === null,
    onExpire: () => failGame('timeout'),
  });

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setSelected(null);
    setGameState('playing');
    setFeedback(null);
    setFoundStatement(null);
    resetSubmission();
  }, [resetSubmission]);

  const handleSelect = (id) => {
    if (gameState !== 'playing') return;
    setSelected(id);

    const statement = statements.find((s) => s.id === id);
    if (statement?.isContradiction) {
      setFoundStatement(statement);
      setFeedback({ type: 'success', message: '矛盾発見！' });
      // 遅延中にタイマー満了で fail し得るため、playing のままの時だけ success に確定する
      setTimeout(() => setGameState((prev) => (prev === 'playing' ? 'success' : prev)), 800);
    } else {
      setFeedback({ type: 'error', message: resolvedConfig.wrongSelectionMessage });
      setTimeout(() => setFeedback(null), 1000);
      // R1 体験文法: 誤選択のミス計上は上限が設定されている時だけ（未設定なら従来挙動のまま）
      if (mistakeLimit != null) {
        const nextMistakes = mistakeCount + 1;
        setMistakeCount(nextMistakes);
        if (nextMistakes >= mistakeLimit) {
          failGame('mistakes');
        }
      }
    }
  };

  // FailOverlay「やり直す」: 内部リセット + retryCount 加算（submit はまだ起きていないので resetSubmission 不要）
  const handleRetry = () => {
    setSelected(null);
    setFeedback(null);
    setFoundStatement(null);
    setMistakeCount(0);
    setFailReason(null);
    setGameState('playing');
    setRetryCount((count) => count + 1);
    resetTimer();
  };

  // HUD チップ: showTimer===false / showMistakeCount===false は表示のみ抑止（計時・上限判定は生きる）
  const hudTimer =
    timeLimitMs != null && remainingMs != null && hostConfig?.showTimer !== false
      ? { remainingMs, totalMs: timeLimitMs }
      : null;
  const hudMistakes =
    mistakeLimit != null && hostConfig?.showMistakeCount !== false
      ? { current: mistakeCount, max: mistakeLimit }
      : null;

  return (
    <div className="t07-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      <div className="t07-bg-overlay absolute inset-0" />

      {/* R1 体験文法: 独自絵文字ヘッダ＋飾りメニューを撤去し、共通 HUD に置換。? は開始ゲート再表示 */}
      <MinigameHud
        title={title}
        icon="TriangleAlert"
        timer={hudTimer}
        mistakes={hudMistakes}
        onHelp={() => setGateOpen(true)}
      />

      <div className="absolute top-14 left-0 right-0 z-20 p-3">
        <div className="t07-facts-panel border rounded-xl p-3">
          <p className="t07-facts-label text-xs font-bold mb-2">📋 既知の事実</p>
          {facts.map((fact, i) => (
            <p key={fact?.id ?? i} className="t07-facts-text text-sm">
              • {typeof fact === 'string' ? fact : fact.text}
            </p>
          ))}
        </div>
      </div>

      <div className="absolute top-44 left-0 right-0 bottom-20 px-4 flex flex-col justify-center gap-3">
        <p className="t07-section-label text-sm text-center mb-2">容疑者の証言</p>
        {statements.map((statement) => (
          <button
            key={statement.id}
            className={`w-full p-4 rounded-xl text-left transition-all border-2
              ${selected === statement.id
                ? statement.isContradiction
                  ? 't07-statement--found'
                  : 't07-statement--neutral'
                : 't07-statement--idle'}`}
            onClick={() => handleSelect(statement.id)}
          >
            <span className="t07-statement-text">{statement.text}</span>
          </button>
        ))}
      </div>

      {feedback && (
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 px-6 py-3 rounded-xl font-bold
          ${feedback.type === 'success' ? 't07-feedback--success' : 't07-feedback--error'}`}>
          {feedback.message}
        </div>
      )}

      <div className="t07-footer absolute bottom-0 left-0 right-0 z-30 pt-4 pb-6 px-4">
        <p className="t07-footer-hint text-sm text-center">矛盾している証言をタップ</p>
      </div>

      {/* R1 体験文法: 開始ゲート（表示中は実動タイマーも停止する） */}
      {gateOpen && (
        <StartGate
          title={title}
          instruction={instruction}
          icon="TriangleAlert"
          onStart={() => setGateOpen(false)}
        />
      )}

      {/* R1 体験文法: fail（時間切れ / ミス上限到達）の明示フィードバック。
          やり直す = 内部リセット / 未整理のまま進む = submit('fail')。文言は config.failMessage 優先 */}
      {gameState === 'fail' && (
        <FailOverlay
          title={pickText(failMessageConfig.title, undefined)}
          subtitle={pickText(failMessageConfig.subtitle, failReason === 'timeout' ? '時間切れ' : 'ミスが上限に達した')}
          allowRetry={failMessageConfig.allowRetry !== false}
          onRetry={handleRetry}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {/* Foundation: 成功モーダル（矛盾の中身バッジ + タイトル + 説明 + 次へ進む を JudgementModal に集約。
          旧: AlertTriangle バッジ → 同義名 TriangleAlert（icons namespace のキーは AlertTriangle 不在））。
          タイトルは config.successMessage.title、解説文は発見した statement の
          contradictionExplanation（無ければ successMessage.subtitle）を表示する。 */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={resolvedConfig.successMessage?.title ?? '矛盾発見！'}
        description={foundStatement?.contradictionExplanation ?? resolvedConfig.successMessage?.subtitle}
        icon={<Icon name="TriangleAlert" size={36} />}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />
    </div>
  );
};

export default Template07Contradiction;
